import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, ChevronDown } from 'lucide-react';
import {
  ChatMessage,
  fetchMessages,
  sendMessage,
  getSessionContactId,
  getUserContactId,
} from '../../services/chatService';

interface ChatWidgetProps {
  /** Logged-in flotillero ID (omit for anonymous / public pages) */
  flotilleroId?: string;
  /** Display name for the contact in respond.io */
  contactName?: string;
  /** Contact email */
  contactEmail?: string;
  /** Extra context to attach to the conversation (e.g. invoice UUID) */
  context?: Record<string, unknown>;
}

const POLL_INTERVAL_MS = 4000;

export const ChatWidget: React.FC<ChatWidgetProps> = ({
  flotilleroId,
  contactName,
  contactEmail,
  context,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastMessageCountRef = useRef(0);

  const contactId = flotilleroId ? getUserContactId(flotilleroId) : getSessionContactId();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load history when chat opens
  const loadMessages = useCallback(async () => {
    const result = await fetchMessages(contactId);
    if (result.success && result.data) {
      setMessages(result.data.messages);
      if (result.data.messages.length > lastMessageCountRef.current && !isOpen) {
        setHasUnread(true);
      }
      lastMessageCountRef.current = result.data.messages.length;
    }
  }, [contactId, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setIsLoadingHistory(true);
      loadMessages().finally(() => {
        setIsLoadingHistory(false);
        setTimeout(scrollToBottom, 100);
      });
      setHasUnread(false);
      inputRef.current?.focus();
    }
  }, [isOpen, loadMessages, scrollToBottom]);

  // Poll for new messages while open
  useEffect(() => {
    if (isOpen) {
      pollRef.current = setInterval(loadMessages, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isOpen, loadMessages]);

  // Scroll when messages change
  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [messages.length, isOpen, scrollToBottom]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;

    // Optimistic update
    const optimistic: ChatMessage = {
      id: `temp_${Date.now()}`,
      direction: 'inbound',
      message_type: 'text',
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    setDraft('');
    setSending(true);

    const result = await sendMessage({
      contactId,
      text,
      contactName,
      contactEmail,
      flotilleroId,
      context,
    });

    if (result.success && result.data) {
      setMessages(prev =>
        prev.map(m => (m.id === optimistic.id ? { ...m, id: result.data!.messageId } : m))
      );
    }

    setSending(false);
    scrollToBottom();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Chat Panel */}
      {isOpen && (
        <div className="w-[360px] max-w-[calc(100vw-2rem)] h-[480px] max-h-[calc(100vh-8rem)] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden animate-scale-in">
          {/* Header */}
          <div className="bg-partrunner-yellow px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-partrunner-black/10 rounded-full flex items-center justify-center">
                <img src="/images/icon-black.png" alt="" className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-partrunner-black leading-tight">
                  Soporte Partrunner
                </p>
                <p className="text-xs text-partrunner-black/60">Te respondemos por aquí</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-partrunner-black/60 hover:text-partrunner-black p-1 rounded-lg hover:bg-partrunner-black/10 transition-colors"
              aria-label="Cerrar chat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50 dark:bg-gray-950">
            {isLoadingHistory && messages.length === 0 && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-partrunner-yellow" />
              </div>
            )}

            {!isLoadingHistory && messages.length === 0 && (
              <div className="text-center py-8 px-2">
                <div className="w-12 h-12 bg-partrunner-yellow/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MessageCircle size={22} className="text-partrunner-yellow-accent" />
                </div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  ¡Hola! ¿En qué podemos ayudarte?
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Escribe tu mensaje y nuestro equipo te responderá lo antes posible.
                </p>
              </div>
            )}

            {messages.map(msg => {
              const isUser = msg.direction === 'inbound';
              return (
                <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                      isUser
                        ? 'bg-partrunner-yellow text-partrunner-black rounded-br-md'
                        : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-md'
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                    <p
                      className={`text-[10px] mt-1 ${
                        isUser ? 'text-partrunner-black/50' : 'text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 bg-white dark:bg-gray-900 shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe un mensaje…"
                className="flex-1 text-sm bg-gray-100 dark:bg-gray-800 border-0 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-partrunner-yellow/40 text-gray-800 dark:text-gray-100 placeholder-gray-400"
                disabled={sending}
              />
              <button
                onClick={handleSend}
                disabled={!draft.trim() || sending}
                className="bg-partrunner-yellow hover:bg-partrunner-yellow-dark text-partrunner-black p-2 rounded-xl transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-partrunner"
                aria-label="Enviar mensaje"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setIsOpen(v => !v)}
        className="group bg-partrunner-yellow hover:bg-partrunner-yellow-dark text-partrunner-black p-4 rounded-2xl shadow-xl shadow-partrunner-yellow/30 transition-all duration-200 hover:scale-105 hover:-translate-y-1 flex items-center justify-center relative"
        aria-label={isOpen ? 'Cerrar chat' : 'Abrir chat de soporte'}
      >
        {isOpen ? (
          <ChevronDown size={28} className="transition-transform duration-200" />
        ) : (
          <MessageCircle
            size={28}
            className="group-hover:scale-110 transition-transform duration-200"
          />
        )}

        {/* Tooltip */}
        {!isOpen && (
          <span className="absolute right-full mr-3 px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none shadow-lg">
            ¿Necesitas ayuda?
          </span>
        )}

        {/* Unread badge */}
        {hasUnread && !isOpen && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-partrunner-red opacity-75" />
            <span className="relative inline-flex rounded-full h-4 w-4 bg-partrunner-red" />
          </span>
        )}
      </button>
    </div>
  );
};
